import type { Ref } from 'react'
import { observer } from 'mobx-react-lite'
import {
  CustomSelect,
  type RenderSearchItemProps,
  SingleItemSelectedDefault,
} from '@/components/inputs/CustomSelect'
import { useCustomSelectStateFromItemSource } from '@/components/inputs/item-source-hook'
import { SearchResultItem } from '@/components/inputs/SearchResultItem'
import { Avatar } from '@/components/Avatar'
import { type Member } from '@/features/iam/members/Member'
import {
  AddClientsAsTeamMemberDialog,
  AddClientsAsTeamMemberSearchItemDetails,
} from '@/features/jobs/details/components/AddClientsAsTeamMemberDialog'
import { type AddClientsAsTeamMemberDialogViewModel } from './AddClientsAsTeamMemberDialogViewModel'

export interface AddClientsAsTeamMemberDialogContainerProps {
  vm: AddClientsAsTeamMemberDialogViewModel
}

export const AddClientsAsTeamMemberDialogContainer = observer(
  function AddClientsAsTeamMemberDialogContainer({
    vm,
  }: AddClientsAsTeamMemberDialogContainerProps) {
    const selectProps = useCustomSelectStateFromItemSource({
      itemSource: vm.fetchTeamMembers,
    })

    const clientError = vm.validation.getError('client')

    return (
      <AddClientsAsTeamMemberDialog
        showing={vm.modal.showing}
        onClose={vm.modal.close}
        onSubmit={vm.submit}
        isSubmitPending={vm.submit.pending}
        isSubmitDisabled={vm.disabled}
        isSubmitRejected={vm.submit.rejected}
        submitError={vm.submit.error}
        clientError={clientError}
        clientSelect={
          <CustomSelect
            {...selectProps}
            multi={false}
            icon="dropdown"
            iconColor="var(--grey600)"
            renderItem={(itemProps) => (
              <SingleItemSelectedDefault
                {...itemProps}
                item={itemProps}
                itemToString={(i) => String(i.item?.displayName)}
                visual={<Avatar size={24} subject={itemProps.item} />}
              />
            )}
            placeholder={'Search team members'}
            value={vm.client}
            onChange={vm.setClient}
            renderSearchItem={(props) => (
              <AddClientSearchItemContainer {...props} />
            )}
            testId="add-job-collaborator-as-team-member-dropdown"
          />
        }
      />
    )
  },
)

interface AddClientSearchItemContainerProps extends RenderSearchItemProps<Member | null> {
  ref?: Ref<HTMLDivElement>
}

const AddClientSearchItemContainer = observer(
  function AddClientSearchItemContainer({
    item,
    itemToString: _itemToString,
    ref,
    ...itemProps
  }: AddClientSearchItemContainerProps) {
    return (
      <SearchResultItem
        {...itemProps}
        ref={ref}
        visual={<Avatar size={24} subject={item} />}
        testId={
          'add-job-collaborator-as-team-member-option-' +
          item?.user.publicId?.toLowerCase()
        }
      >
        <AddClientsAsTeamMemberSearchItemDetails
          name={item?.displayName}
          email={item?.user.email}
        />
      </SearchResultItem>
    )
  },
)
